import { makeAutoObservable } from "mobx"
import UserAPI from "../api/user";
import { BaseStore } from "./base_store";
import { api_post } from 'api/utils';
import { api_get } from 'api/utils';

type PublishedUserResponseTyped = {
    payload: UserProps;
    job_1: string;
}
type UserStoreAssignableList = {
    items: UserProps[];
}

export type UserProps = {
	key: string;
    ogkey: string;
    uuid: string;
    verified_uuid: string;
    has_calendar: boolean;
    synced_calendars: string[];
    calendar_preferences: string;
    name: string;
	first_name: string;
	last_name: string;
	primary_email: string;
    invited: string;
    current_credit: number;
    credit_included: number;
    tasks_remaining: number;
    next_billing_cycle_date: string;
    subscription_product: string;
    subscription_service: string;
    stripe_customer_id: string;
    recurly_account_code: string;
	date_created: string;
    allowed_identities: number;
    is_ic: boolean;
    is_beta: boolean;
    is_disabled: boolean;
    timezone_city?: string;
    phone_number?: string;
    home_street_1?: string;
    home_street_2?: string;
    home_city?: string;
    home_state?: string;
    home_zip?: string;

    user_agent?: string;
    header_ip_addr?: string;
    header_ip_country?: string;
    header_ip_city?: string;
    header_ip_region?: string;
    header_ip_regioncode?: string;

	updateProps: (attributes: UserProps) => void;
};

// Domain object User
export class User implements UserProps {
	key = "";
    ogkey = "";
    uuid = "";
    verified_uuid = "";
    has_calendar = false;
    synced_calendars = [];
    calendar_preferences = "";
    name = "";
	first_name = "";
	last_name = "";
	primary_email = "";
    invited = "";
    current_credit = 0;
    credit_included = 0;
    tasks_remaining = 0;
    next_billing_cycle_date = "";
    subscription_product = "";
    subscription_service = "";
    stripe_customer_id = "";
    recurly_account_code = "";
    allowed_identities = 0;
    is_ic = true;
    is_beta = false;
    is_disabled = false;
    timezone_city = "";
    phone_number = "";
    home_street_1 = "";
    home_street_2 = "";
    home_city = "";
    home_state = "";
    home_zip = "";
    user_agent = "";
    header_ip_addr = "";
    header_ip_country = "";
    header_ip_city = "";
    header_ip_region = "";
    header_ip_regioncode = "";
	date_created = "";

	constructor(attributes: UserProps) {
		makeAutoObservable(this)
		if(attributes) {
			this.updateProps(attributes);
		}
	}

	updateProps(attributes: UserProps) {
		Object.assign(this, attributes);
	}

}


export class UserStore extends BaseStore<UserProps, User> {

    CACHED_USERS: Map<string, UserProps> = new Map();

	modelConstructor() {
		return User;
	}


    async updateField(field: string, value: string) {
        return api_post('/users/update-field', { field, value })
            .then(async (res) => {
                const j = await res.json() as UserProps;
                return j;
            });
    }

    async supportEmailSearch(identity: string, query: string, cursor="") {
        return UserAPI.supportEmailSearch(identity, query, cursor).then((res) => {
            return res.items as UserProps[];
        });
    }

    async supportUserFetch(identity: string, userKey: string) {
        if(this.CACHED_USERS.has(userKey)) {
            return this.CACHED_USERS.get(userKey) as UserProps;
        }
        return UserAPI.supportUserFetch(identity, userKey).then((res) => {
            this.CACHED_USERS.set(userKey, res);
            return res as UserProps;
        });
    }

    async supportAssignableUsers(workKey: string) {
        return api_get('/users/assignable', {identity: workKey}).then(async (res) => {
            const j = await res.json() as UserStoreAssignableList;
            return j.items as UserProps[];
        });
    }

    async sudoSetAllowedIdentities(userKey: string,
        allowedIdentities: number,
        sudo_identity: string) {
        const data = {
            user: userKey,
            allowed_identities: allowedIdentities,
            identity: sudo_identity
        };
        return api_post('/users/sudo-set-allowed-identities', data).then(async (res) => {
            const j = await res.json() as PublishedUserResponseTyped;
            return j.payload as UserProps;
        });
        }

    async sudoUpdateICStatus(workKey: string, user_uuid: string, is_ic: boolean) {
        const data = {
            identity: workKey,
            user_uuid,
            is_ic: is_ic ? "yes" : "no"
        };
        return api_post('/users/sudo/set-ic', data).then(async (res) => {
            const j = await res.json() as UserProps;
            return j;
        });
    }

    async managerUserCreate(workKey: string,
        primary_email: string,
        stripe_customer_id: string,
        recurly_account_code: string,
        subscription_service: string,
        subscription_product: string) {
        const data = {
            identity: workKey,
            primary_email,
            stripe_customer_id,
            recurly_account_code,
            subscription_service,
            subscription_product
        };
        return api_post('/users/manager/user-create', data).then(async (res) => {
            const j = await res.json() as UserProps;
            return j;
        });
        }

    async signupEmailStart(email: string) {
        const data = {
            email
        };
        return api_post('/users/signup-email-start', data)
            .then(async () => {
                return true;
            })
            .catch(() => {
                return false;
            });
    }

	async loadCurrentUser() {
		/* you not use this, load from auth_context */
		const data = await UserAPI.load() as UserProps;
		if(data) {
			return new User(data);
		}
		else {
            console.log("No Current User")
			return null;
		}
	}


  async sudoUpdateIsBeta(workKey: string, user_uuid: string, is_beta: boolean) {
        const data = {
            identity: workKey,
            user_uuid,
            is_beta: is_beta ? "yes" : "no"
        };
        return api_post('/users/sudo/set-is-beta', data).then(async (res) => {
            const j = await res.json() as UserProps;
            return j;
        });
    }
  async sudoUpdateIsDisabled(workKey: string, user_uuid: string, is_disabled: boolean) {
        const data = {
            identity: workKey,
            user_uuid,
            is_disabled: is_disabled ? "yes" : "no"
        };
        return api_post('/users/sudo/set-is-disabled', data).then(async (res) => {
            const j = await res.json() as UserProps;
            return j;
        });
  }
    async managerSetSubscriptionService(workKey: string, user_uuid: string, subscription_service: string) {
        const data = {
            identity: workKey,
            user_uuid,
            subscription_service
        };
        return api_post('/users/manager/set-subscription-service', data).then(async (res) => {
            const j = await res.json() as UserProps;
            return j;
        });
    }

    async managerSetSubscriptionProduct(workKey: string, user_uuid: string, subscription_product: string) {
        const data = {
            identity: workKey,
            user_uuid,
            subscription_product
        };
        return api_post('/users/manager/set-subscription-product', data).then(async (res) => {
            const j = await res.json() as UserProps;
            return j;
        });
    }

    async managerSetSubscriptionCustomerId(workKey: string, user_uuid: string, subscription_customer_id: string) {
        const data = {
            identity: workKey,
            user_uuid,
            subscription_customer_id
        };
        return api_post('/users/manager/set-subscription-customer-id', data).then(async (res) => {
            const j = await res.json() as UserProps;
            return j;
        });
    }

    async managerSetRecurlyIncludedMinutes(workKey: string, user_uuid: string, minutes: number) {
        const data = {
            identity: workKey,
            user_uuid,
            minutes
        };
        return api_post('/users/manager/set-recurly-included-minutes', data).then(async (res) => {
            const j = await res.json() as UserProps;
            return j;
        });
    }

}
